import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SHOP_PRODUCTS } from "@/lib/shopCatalog";
import { trackBeginCheckoutBundle } from "@/lib/analytics";

const TEE_KINDS = ["skyline", "area", "color"];
export const BUNDLE_TEES = SHOP_PRODUCTS.filter(
  (p) => TEE_KINDS.includes(p.kind) && p.status === "available" && p.price
);

const TeePicker = ({ label, idx, slug, size, onSlug, onSize }) => {
  const p = BUNDLE_TEES.find((t) => t.slug === slug);
  return (
    <div className="border border-white/15 p-5" data-testid={`bundle-picker-${idx}`}>
      <p className="font-mono-label text-white/40">{label}</p>
      <div className="flex flex-col sm:flex-row gap-4 mt-4">
        {p && (
          <img
            src={p.image}
            alt={p.alt}
            width="112"
            height="112"
            loading="lazy"
            className={`h-24 w-24 lg:h-28 lg:w-28 object-cover border border-white/10 shrink-0${p.color_art ? "" : " img-bw"}`}
          />
        )}
        <div className="flex-1 min-w-0">
          <select
            data-testid={`bundle-select-${idx}`}
            value={slug}
            onChange={(e) => { onSlug(e.target.value); onSize(""); }}
            className="w-full bg-transparent border border-white/25 font-mono-label text-white px-3 py-3 focus:border-white outline-none [&>option]:bg-black [&>option]:text-white"
          >
            {BUNDLE_TEES.map((t) => (
              <option key={t.slug} value={t.slug}>{`${t.seo_name.toUpperCase()} — $${t.price}`}</option>
            ))}
          </select>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(p?.sizes || []).map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`bundle-size-${idx}-${s.toLowerCase()}`}
                onClick={() => onSize(s)}
                className={`font-mono-label border px-3 py-2.5 transition-colors duration-200 ${
                  size === s ? "bg-white text-ink border-white" : "border-white/25 text-white/60 hover:border-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const BundleBuilder = ({ defaultSlug, hideHeader = false }) => {
  const first = BUNDLE_TEES.find((t) => t.slug === defaultSlug) || BUNDLE_TEES[0];
  const second = BUNDLE_TEES.find((t) => t.slug !== first?.slug) || first;
  const [slugA, setSlugA] = useState(first?.slug || "");
  const [slugB, setSlugB] = useState(second?.slug || "");
  const [sizeA, setSizeA] = useState("");
  const [sizeB, setSizeB] = useState("");
  const [loading, setLoading] = useState(false);

  if (BUNDLE_TEES.length < 2) return null;
  const pA = BUNDLE_TEES.find((t) => t.slug === slugA);
  const pB = BUNDLE_TEES.find((t) => t.slug === slugB);
  const value = (pA?.price || 0) + (pB?.price || 0);

  const buy = async () => {
    if (!sizeA || !sizeB) {
      toast.error("Pick a size for both tees.");
      return;
    }
    setLoading(true);
    trackBeginCheckoutBundle([{ p: pA, size: sizeA }, { p: pB, size: sizeB }]);
    try {
      const { data } = await api.post("/payments/checkout-bundle", {
        items: [
          { lookup_key: slugA, size: sizeA },
          { lookup_key: slugB, size: sizeB },
        ],
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="bundle-builder" className="border border-white/20 bg-white/[0.03] p-6 lg:p-8">
      {!hideHeader && (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <p className="font-mono-label text-white/40">CITY TEE BUNDLE</p>
              <h3 className="font-display text-2xl lg:text-3xl mt-2">ANY TWO CITY TEES. $75 FLAT.</h3>
            </div>
            <p className="font-mono-label text-white/50" data-testid="bundle-savings">
              <span className="line-through text-white/30">${value}</span> → $75 · SAVE ${value - 75}
            </p>
          </div>
          <p className="text-white/50 text-sm mt-3 max-w-2xl">
            Rep your city and the one you race in. Pick any two City Series tees — skyline, area-code or Color Series, any
            sizes — and the pair ships for $75 flat.
          </p>
        </>
      )}
      {hideHeader && (
        <p className="font-mono-label text-white/50" data-testid="bundle-savings">
          <span className="line-through text-white/30">${value}</span> → $75 · SAVE ${value - 75}
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <TeePicker label="TEE ONE" idx={1} slug={slugA} size={sizeA} onSlug={setSlugA} onSize={setSizeA} />
        <TeePicker label="TEE TWO" idx={2} slug={slugB} size={sizeB} onSlug={setSlugB} onSize={setSizeB} />
      </div>
      <button
        data-testid="bundle-buy-button"
        onClick={buy}
        disabled={loading}
        className="btn-invert font-mono-label bg-white text-ink border border-white w-full md:w-auto px-8 py-4 mt-5 hover:bg-transparent hover:text-white transition-colors duration-200 disabled:opacity-50"
      >
        {loading ? "OPENING CHECKOUT..." : "BUNDLE BOTH · $75"}
      </button>
    </div>
  );
};
